import React, { useState, useEffect, useRef, useContext } from 'react';
import ReactDOM from 'react-dom/client';
import DOMPurify from 'dompurify';

import { MultiCarouselR, MultiCarouselFlex } from './multiCarousel.js';
import { QuickViewer } from './quickView.js';
import { ItemOverlay } from './itemOverlay.js';
import { SearchConfigProvider, SearchConfigContext } from '../context/searchConfigContext.js';
import { FALLBACK_IMAGE_SIZES, getFallbackImageUrl, useElasticSearch, sizeResettingErrorHandler } from '../util';

const showOnlyAboutTab = () => {
    // Finds the anchor tag with the data-target selector and click it

    const rels = document.querySelector(".tabs li a[data-target='section-relationships']");
    if ( rels !== null ) {

      rels.parentElement.classList.add("is-hidden")
      rels.parentElement.classList.remove("is-active")
      rels.parentElement.classList.remove("active-tab")

      let contentElement = document.getElementById(rels.dataset.target)
      contentElement.style.display = 'none'

    }

    const about = document.querySelector(".tabs li a[data-target='section-about']");
    if ( about !== null ) {
      about.parentElement.classList.add('is-active')
      about.parentElement.classList.add('active-tab')

      let contentElement = document.getElementById(about.dataset.target)
      contentElement.style.display = 'block'
    }

}

const normalizeRelationshipData = (dataItem,mapping) => {
    const itemId =  mapping.link(dataItem);
    const imageUrl = mapping.imageUrl(dataItem);
    const imageSrcSet = mapping.imageSrcSet(dataItem);
    const artist = mapping.makers(dataItem);
    const title = mapping.label(dataItem);
    const year = mapping.dateLabel(dataItem);
    const credit = mapping.credit(dataItem)
    const identifier = mapping.accession(dataItem);
    const collection = mapping.currentOwner(dataItem);
    const copyright = mapping.copyright(dataItem);
    const spanishLink = dataItem?._source?.site_link?.localizations?.es?.link ?? ''
    // this data will be given to the QV popup on opening:
    return {
        imageUrl, title, artist, year, credit, identifier, collection, copyright, spanishLink,
        'id': itemId,
        // 'imageId': props.item.image_id,
        'thumbnail': imageUrl,
        'imageSrcSet': imageSrcSet,
        // 'listIdx': props.listIdx,   // or, idx in list
        // // including the list allows the QV to operate a MultiCarousel of items:
        // 'myList': props.myList, // FIXME: This is _all_ data
        'dataFormat': 'relationships'
    }

}

const RelationshipListItem = (props) => {
    // Mapping / schema
    const mapping = props.mapping

    const itemId = props.showAll ? props.item.link : mapping.link(props.item);
    const imageUrl = props.showAll ? props.item.thumbnail : mapping.imageUrl(props.item);
    const imageSrcSet = props.showAll ? props.item.thumbnail : mapping.imageSrcSet(props.item);
    const artist = props.showAll ? '' : mapping.makers(props.item);
    const title = props.showAll ? props.item.label['@none'][1] : mapping.label(props.item);
    const year = props.showAll ? '' : mapping.dateLabel(props.item);
    const identifier = props.showAll ? '' : mapping.accession(props.item);
    const collection = props.showAll ? '' : mapping.currentOwner(props.item);
    const copyright = props.showAll ? '' : mapping.copyright(props.item);
    const spanishLocalizations = props.showAll ? '' : props.item._source?.site_link?.localizations?.es ?? ''
    const spanishLinkHref = spanishLocalizations.link ? { href: spanishLocalizations.link } : {}

    // this data will be given to the QV popup on opening:
    const itemData = {
        imageUrl, title, artist, year, identifier, collection, copyright,
        'id': itemId,
        'imageId': props.item.image_id,
        'thumbnail': imageUrl,
        'listIdx': props.listIdx,   // or, idx in list
        // including the list allows the QV to operate a MultiCarousel of items:
        'myList': props.myList.map( qvListItem => normalizeRelationshipData(qvListItem,mapping)), // FIXME: This is _all_ data but we should use callbacks
        'dataFormat': 'relationships'
    }

    // PROBLEM: in some browsers, the onfocus and onclick events seem to happen in an indeterminate order.
    // esp. chrome on mac
    // the result is that sometimes, in those browsers, the onclick event doesn't trigger opening the QV.
    // SOLUTION: break down the 'click' event into mousedown + mouseup
    // in onmousedown, preventDefault so the focus doesn't happen
    // in onmouseup, do the focus stuff
    // NOTES: onclick would handle the Enter keypress, but onmouseup doesn't, so we need an extra handler for the Enter key

    const goToItem = (id) => {
        // FIXME: Replace this with a traditional anchor link (better a11y)
        if (id.search(/^https?:\/\//) === 0) {
            window.open(id,'_blank').focus()
        } else {
            window.open(id,'_self');
        }
    }

    const itemMouseDownHandler = (event) => {
        event.preventDefault();
    }

    const itemMouseUpHandler = (event, id) => {
        if (event.button !== 0) {
            // ignore if not the main (usually left) mouse button
            return;
        }
        //console.log('itemMouseUpHandler');
        if (event.target.classList.contains('grid-object-overlay-qv')) {
            // we'll let the overlay click handler handle it
            // console.log('no-op');
        } else if (event.target.classList.contains('relationship-item-additional-link')) {
            // no-op, just let the anchor href handle it
        } else {
            console.log(`going to ${id}`)
            goToItem(id);
        }

        // make sure clicked item has focus - see https://zellwk.com/blog/inconsistent-button-behavior/
        // event.currentTarget.classList.toggle('focus-outline-none', true);   // disable focus outline, only when clicking, not tabbing
        // event.currentTarget.focus();
        // props.storeActive();
    }

    const itemFocusHandler = (event) => {
        if (props.focusfunc) props.focusfunc(event);
    }

    const itemKeyUpHandler = (event, id) => {
        if (event.key === 'Enter') {
            // console.log('enter key');
            goToItem(id);
            // props.storeActive();
        }
    }

    // open the quick view
    const itemQuickViewHandler = (event) => {
        if (event.button !== 0) {
            // ignore if not the main (usually left) mouse button
            return;
        }
        console.log('itemQuickViewHandler');
        props.itemClickAction(event, itemData);
        // make sure clicked item has focus - see https://zellwk.com/blog/inconsistent-button-behavior/
        event.currentTarget.classList.toggle('focus-outline-none', true);   // disable focus outline, only when clicking, not tabbing
        event.currentTarget.focus();
        // props.storeActive();
        event.stopPropagation();
    }

    // anchorOptions gets spread across the component to allow for empty values without creating empty attribs on the element
    var anchorOptions = {};
    if (!props.overlay) {
        anchorOptions = {href: itemId}
    }

    let srcSet = `${imageUrl.replace(',242', ',242')} 1x, ${imageUrl.replace(',242', ',484')} 2x, ${imageUrl.replace(',242', ',726')} 3x`
    let imgSizeParams = imageUrl.endsWith(".svg") ? {height: "240", width: "240"} : {}

    let collectionLabel
    switch (true) {
        case (Boolean(props.item?._source?.cr_number)):
            collectionLabel = `${mapping.currentOwner(props.item)} (CR ${props.item._source.cr_number})`
            break

        case (props.item?._source?.type?.includes("art") || props.item?._source?.type?.includes("art-ref")):
            collectionLabel = mapping.currentOwner(props.item)
            break

        default:
            collectionLabel = "Georgia O'Keeffe Museum"
    }

    // NOTE: the ID below must be unique
    return (
        <a {...anchorOptions}
        id={props.id}
        className={`results-grid-item relationship-grid-item`}
        tabIndex="0"
        onMouseUp={(e) => itemMouseUpHandler(e, itemId)}
        onFocus={itemFocusHandler}
        onMouseDown={itemMouseDownHandler}
        onKeyUp={(e) => itemKeyUpHandler(e, itemId)}>
            <div className="section-archive-series-details-card">
                {/* inline-block display needed to meet the specs for wrapping the constrained text */}
                <div className="results-grid-item-inline-ctnr">
                    <div className="results-grid-item-image">
                        <div className={`results-grid-item-visible-image`}>
                            { !props.showAll ? <React.Fragment><img className={`thumb`}
                                alt=""
                                loading="lazy"
                                key={imageUrl}
                                srcSet={srcSet}
                                onError={sizeResettingErrorHandler()}
                                {...imgSizeParams}
                                />
                                <ItemOverlay clickFunction={itemQuickViewHandler} /></React.Fragment> : <div className={`${props.showAll ? 'show-all-card': ''}`}></div> }
                        </div>
                    </div>
                    <div className="results-grid-width-constrained section-archive-series-details-card-text is-size-7 has-text-weight-semibold section-archive-series-details-card-text-creators has-text-grey-dark" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(artist, {ALLOWED_TAGS: ['br']})}}>
                    </div>
                    <div className={`results-grid-width-constrained relationship-item-title section-archive-series-details-card-text is-size-6 has-text-weight-semibold ${ props.showAll ? 'has-text-centered' : '' }`}>
                        {title}
                    </div>
                    <div className="results-grid-width-constrained section-archive-series-details-card-text is-size-7 has-text-grey">
                        {year}
                    </div>
                    <div className={`results-grid-width-constrained section-archive-series-details-card-text is-size-7 has-text-grey`}>
                        {collectionLabel}
                    </div>
                    <div className={`results-grid-width-constrained section-archive-series-details-card-text has-text-weight-semibold ${ spanishLocalizations ? '' : 'd-none' }`}>
                        <a className="is-size-7 relationship-item-additional-link" {...spanishLinkHref} target="_blank">{`(en español)`}</a>
                    </div>
                </div>
            </div>
        </a>
    )
}

export const RelationshipsViewer = (props) => {

    // FIXME: For exhib rels:
    // - [x] _only_ show direct rels (use filterFunc?)
    // - w/ counts for each P2 type, so:
    //
    // - Relationships
    //   - Archives and Publications
    //      - 2 Invitations
    //      - 1 Exhibition Catalogue
    //      - 20 Exhibition Installation Photographs

    // Elastic boilerplate
    const [ endpoint, setEndpoint ] = useState(props.endpoint)
    const [ query, setQuery ] = useState(null)
    const [ mapping, setMapping ] = useState(props.mapping)
    const [ relationshipsLoaded, setRelationshipsLoaded ] = useState(false)

    const [data, setData] = useState({});
    const [extantCategories, setExtantCategories] = useState([]);

    let results = useElasticSearch(endpoint,query);

    const presentInResults = (ident,resultResponse) => {

        if (resultResponse) {
            if (ident.startsWith('exhibition/')) {
                return resultResponse.hits.hits.some( hit => hit._source.venues?.some( v => v.id.endsWith(ident) ) ) ?? false
            }

            return resultResponse.hits.hits.some( hit => hit._id === ident ) ?? false

        } else {
            return false
        }
    }

    useEffect(() => {
        if (Boolean(results)) {
            return
        }

        const fetchResults = async () => {
            try {
                const response = await fetch('relationships.json', { method: 'GET' })
                const data = await response.json()

                setData(mapping.dataNormalizer(data));
                let ids = Object.entries(data).flatMap( ([relType,relData]) => { return relData.flatMap( rel => rel.items.map( itemData => itemData.id ) ) }  )

                // FIXME: `ids` is all ids in the relationship, but we need to only ask for exhibition/ items as venues
                let hasVenues = ids.some( i => i.startsWith('exhibition/') )
                let query = { query: {}, size: ids.length }

                if (hasVenues) {
                    let nonVenueIDs = ids.filter( i => !i.startsWith('exhibition/') )
                    let venueIds = ids.filter( i => i.startsWith('exhibition/') )
                    query = { query: { bool: { must: [ { bool: { should: venueIds.map( i => { return { term: { "venues.id": `http://data.okeeffemuseum.org/${i}` } } } ).concat( { ids: { values: nonVenueIDs } } ) }}]} }, size: ids.length }
                } else {
                    query.query = { ids : { values: ids } }
                }
                setQuery(query)

                const headers = mapping.headers(data)
                const headersInData = Object.keys(headers).filter( headerKey => headerKey in data )
                setExtantCategories([...headersInData]);

                // If we don't have any relationships to show, remove the rels tab and set the about tab to display
                if (Object.keys(data).length === 0) {
                    showOnlyAboutTab();
                }
            } catch (error) {
                console.error("Error:",error)
                setData({})
            }            
        }

        fetchResults()
    }, []);

    const [ qvData, setQvData ] = useState(props.qvData);

    const openTheQV = (e, data) => {
        setQvData(data);    // this will trigger the qv to open
    }

    return (
    <div className="mx-0">
        <div className='section-relationships-r'>
        {
            extantCategories.map((category, catIdx) => {
                return (
                <div className="mx-0 px-0" key={catIdx}>
                    {/* Hide top bar if the relationship section is role - Temp solution till a new template is created for the page*/}
                    <div className="relationship-header">
                      {(category !== 'roles' && category !== 'exhibition-organizer' && category !== 'exhibition-roles' ?
                      <div className="rels-heading bg-bone-180">
                          <div className="page-headline">{mapping.headers(data)[category]}</div>
                      </div> : '' )}
                      {(category === 'in_collection' ?
                      <div className="see-all-link-container">
                          <a href={data[category]?.at(0)?.link ?? ''}>See all [{data[category]?.at(0)?.count ?? ''}] items</a>
                      </div> : '' )}
                    </div>
                    {
                        data[category].map((relationship, idx) => {
                            const seeAllLink = relationship.link;
                            const allItemsCount = relationship.count;
                            const klass = `${relationship.class}-relationship-${idx}`;
                            const count = relationship.label['@none'][0].replace(/<\/?em>/g,"");
                            const desc = relationship.label['@none'][1];
                            const year = relationship.label['@none'][2];
                            const eltIdPrefix = `rel-${category}-${klass}-${idx}`;

                            const shouldShowAllCard = allItemsCount > relationship.items.length;
                            return (
                            <div key={idx} className="mx-0">
                                {/* Show top bar if the relationship section is role - Temp solution till a new template is created for the page*/}
                                {(relationship.class === 'roles' || relationship.class === 'exhibition-organizer' || relationship.class == 'exhibition-roles' || relationship.class == 'in_collections'?
                                    <div className="rels-heading bg-bone-180">
                                        <div className="page-headline">{count}
                                        </div>
                                    </div> : ''
                                )}
                                {/* NOTE: be sure the id below is unique in the document */}
                                <div className="columns is-gapless is-mobile rel-desc-plus-mc" id={`section-relationships-${klass}-${idx}`}>
                                    {/* Hide left block if the relationship section is role or depitcs - Temp solution till a new template is created for the page*/}
                                    <div id={`section-relationships-${klass}-${idx}-info`} className={`${ [ 'depicts', 'roles', 'exhibition-roles', 'exhibition-organizer', 'mentioned_in', 'in_collection' ].includes(relationship.class) ? "d-none" : "column is-12-mobile is-4-tablet"}`} >
                                        {(relationship.class === 'exhibition' ?
                                          <div className="exhib-section rel-heading-block">
                                            <div className="count-section">
                                                <div className="relationship-count-text" id={`section-relationships-${klass}-${idx}-count`}>{ count } </div>
                                                <span className="section-relationships-desc"></span>
                                            </div>
                                            <button className="exhib-btn rel-desc-container">
                                                <div className="section-relationships-desc" id={`section-relationships-${klass}-${idx}-desc`}><a href={`${seeAllLink}`} class="">{desc}</a></div>
                                                <div className="section-relationships-year" id={`section-relationships-${klass}-${idx}-year`}>{year}</div>
                                            </button>
                                          </div>
                                          :
                                          <div className="d-flex align-items-center rel-heading-block pt-5 pb-3">
                                          <div className="relationship-count-text" id={`section-relationships-${klass}-${idx}-count`}>{(relationship.class === "depicts" ? 'DEPICTED IN' : count)}</div>
                                          <div className="pr-3 rel-desc-container">
                                              <div className="section-relationships-desc" id={`section-relationships-${klass}-${idx}-desc`}>{desc}</div>
                                              <div className="section-relationships-year" id={`section-relationships-${klass}-${idx}-year`}>{year}</div>
                                          </div>
                                      </div>)}

                                    </div>
                                    {/* Make right block full width if the relationship section is role or depitcs - Temp solution till a new template is created for the page*/}
                                    <div className={`${relationship.class === "depicts" || relationship.class === "roles" || relationship.class === "exhibition-organizer" || relationship.class === "exhibition-roles" || relationship.class === "in_collection" ? "column is-12-mobile is-12-tablet rel-mc-container" : "column is-12-mobile is-8-tablet rel-mc-container"}`}>
                                    <MultiCarouselFlex
                                        eltIdPrefix={eltIdPrefix}
                                        buttonClass={`sliding-button-120`}
                                        preload={false}
                                    >
                                    {
                                        relationship.items.filter( item => presentInResults(item.id,results) || item.id.startsWith('https://publications.') ) // FIXME: Kludge
                                            .map((item, idx1) => {

                                            var listItem = {}
                                            if (results && results.hits.hits.find( hit => hit._id === item.id ))  {
                                                listItem = results.hits.hits.find( hit => hit._id === item.id )
                                            } else if (item.id.includes('exhibition/')) {
                                                let parentVenue = results.hits.hits.find( hit => hit._source.venues.some( v => v.id.endsWith(item.id) ) )
                                                listItem = { _id: item.id, _source: parentVenue._source }
                                            } else if (item.id.startsWith('https://publications.')) {
                                                listItem = {
                                                    _id: item.id,
                                                    _source: {
                                                        item_type: [item.type],
                                                        artist_label: [ item.label['@none'][0] ],
                                                        label: [ item.label['@none'][1] ],
                                                        representation: {
                                                            primary: item.image_id
                                                        },
                                                        site_link: item.site_link,
                                                        timespan: {
                                                            label: [item.label['@none'][2]]
                                                        }
                                                    }
                                                }

                                            }

                                            return <RelationshipListItem
                                                itemClickAction={openTheQV}
                                                key={idx1}
                                                id={`${eltIdPrefix}-${idx1}`}   // note: items in MultiCarouselFlex MUST have a unique id
                                                // FIXME: Pass pub data here if it's a pub
                                                item={listItem}
                                                mapping={mapping}
                                                // FIXME: .. And there
                                                myList={ relationship.items.map( item => {
                                                                                    if (item.id.startsWith('https://publications.')) {
                                                                                        return listItem
                                                                                    } else if (item.id.startsWith('exhibition/')) {
                                                                                        let pv = results.hits.hits.find( hit => hit._source.venues.some( v => v.id.endsWith(item.id) ) )
                                                                                        let li = { _id: item.id, _source: pv._source }

                                                                                        return li
                                                                                    } else {
                                                                                        return results.hits.hits.find( hit => item.id === hit._id )
                                                                                    }
                                                                            })
                                                                            .filter( hit => hit ) }
                                                listIdx={idx1}
                                                overlay={true}
                                                showAll={false}
                                            />})
                                            .concat(
                                            shouldShowAllCard ?
                                            <RelationshipListItem
                                                itemClickAction={ () => console.log("FIXME: Should go to show-all link") }
                                                key={`${idx}-show-all`}
                                                id={`${idx}-show-all`}
                                                item={{id: `${idx}-show-all`, image_id: '/assets/svg/icon_view_all.svg', thumbnail: '/assets/svg/icon_view_all.svg', link: seeAllLink, label: {'@none':['','View All','']} }}
                                                myList={[]}
                                                listIdx={`${idx}-show-all`}
                                                mapping={mapping}
                                                overlay={false}
                                                showAll={true}
                                            /> : <div key={`${idx}-show-all`} className="is-hidden"></div>
                                            )
                                    }
                                    </MultiCarouselFlex>
                                    </div>
                                </div>
                            </div>
                            )
                        })
                    }
                </div>
                )
            })
        }

        <QuickViewer
            myId='qvPanel-relationships'
            qvData={qvData}     // when this data is non-null it triggers the thing to open
            preloadClickedImg={false}
            relationshipsMapping={mapping}
        />

        </div>
    </div>
    )
}

window.addEventListener('DOMContentLoaded', (event) => {

    if (document.getElementById('relationshipsViewerRoot')) {
        let dataset = document.getElementById('relationshipsViewerRoot').dataset;

        let endpointPath = dataset.indexName ? `${dataset.indexName}/_search`: 'entity/_search'
        let endpointUrl = new URL(endpointPath, dataset.endpoint);

        let mapping = {
            headers: (relationships) => {
                    switch (dataset.entityType) {
                        case 'exhibition':
                            return { 'date': 'Archives and Publications',
                                    'in_collection': 'In Collection',
                                    'mentioned_in': 'Mentioned In'}
                        default:
                            return { 
                                'in_collection': 'In Collection',
                                'sets': 'Related Sets',
                                'topics': 'Related Works',
                                'direct': 'Direct References',
                                'mentioned_in': 'Mentioned In',
                                'date': 'Archives',
                                'theme': 'Theme',
                                'exhibition': 'Exhibitions',
                                'depicts': 'Depicted In',
                                'people': 'People',
                                'exhibition-organizer': '',
                                'exhibition-roles': '',
                                'roles': '' }
                    }
            },
            dataNormalizer: (relationships) => {
                switch (dataset.entityType) {
                    case 'exhibition-roles':
                    case 'exhibition-organizer':

                        var types = {};

                        relationships.direct?.forEach( rel => rel.items.forEach( item => {
                            if (!(item.type in types)) {
                                types[item.type] = []
                            }

                            types[item.type].push(item)
                        }));

                        let result = Object.entries(types).map( ([type,items]) => {
                            return {
                                class: "direct",
                                label: {
                                    "eng": [
                                    `${items.length}`,
                                    type
                                    ],
                                    "@none": [
                                    `${items.length}`,
                                    type
                                    ]
                                },
                                items: items,
                                count: items.length,
                                rank: 0
                            }
                        })

                        delete relationships['direct']
                        // console.log(result)
                        return { 'direct': result, ...relationships }

                    default:
                        return relationships
                }
            },
            displayType: (dataSource) => { return 'Artwork' },
            dateLabel: (dataSource) => {

              if ( typeof dataSource._source.timespan?.label === "string" ) {
                return dataSource._source.timespan?.label
              } if ( Array.isArray(dataSource._source.timespan?.label) ) {
                return dataSource._source.timespan?.label.join(', ')
              } else {
                return dataSource._source.timespan?.label ?? ''
              }
            },
            makers: (dataSource) => { return dataSource._source.artist_label?.join('<br>') ?? '' },
            materials: (dataSource) => { return dataSource._source.classified_as?.join(', ') ?? '' },
            dimension: (dataSource) => { return dataSource._source.extent?.join(', ') ?? '' },
            isArtObject: (dataSource) => { return (dataSource._source) && (dataSource._source.type) && (typeof dataSource._source.type == 'string') && (dataSource._source.type == 'art') ? true : false },
            isReference: (dataSource) => { return false }, // For now, all relationships are in-GOKM
            label: (dataSource) => { return Array.isArray(dataSource._source.label) ? dataSource._source.label.join('') : dataSource._source.label },
            link: (dataSource) => { return dataSource._id.startsWith('http://') || dataSource._id.startsWith('https://') ? dataSource._id : `/${dataSource._id}` },
            imageSrcSet: (dataSource,baseWidth=242,densities=[1,2,3]) => {
              let primaryUrl = dataSource._source.representation?.primary;

              if (primaryUrl === undefined || primaryUrl === null) {
                return primaryUrl
              }

              let height = dataSource._source.representation?.height ?? 9999;
              let width = dataSource._source.representation?.width ?? 9999;

              let url = new URL(primaryUrl);
              let iiifParams = url.pathname.split('/').slice(-4).join('/');

              return densities.map( density => {

                  let densityParams = [ 'full', `,${ Math.min( Number(height), baseWidth*density) }`, '0', 'default.jpg' ].join('/')
                  let imageIdPath = primaryUrl.replace(iiifParams,densityParams)

                  return `${imageIdPath} ${Number(density)}x`;

                }).join(', ')
            },
            imageUrl: (dataSource) => { return dataSource._source.representation?.primary?.replace('square/!240,240', 'full/,242') ?? getFallbackImageUrl(dataSource,FALLBACK_IMAGE_SIZES.LARGE) }, // returns URL
            description: (dataSource) => { return '' },
            credit: (dataSource) => {
              return ( dataSource._source.caption?.credit ?? []).join(', ');
            },
            copyright: (dataSource) => {
              return ( dataSource._source.caption?.rights ?? []).join(', ') ;
            },
            currentOwner: (dataSource) => {
              return ( dataSource._source.caption?.owner ?? []).join(', ') ;
            },
            accession: (dataSource) => {
              return ( dataSource._source.accession ?? []).join(', ') ;
            }
        }

        ReactDOM.createRoot(document.getElementById('relationshipsViewerRoot')).render(<RelationshipsViewer mapping={{...mapping}} endpoint={endpointUrl.href} showAllCard={true} />);
    }

});
